'use client';

import { useState } from 'react';

import { Check, Copy } from 'lucide-react';

import { Button } from '@kit/ui/button';
import { Trans } from '@kit/ui/trans';

/**
 * Die Fehlerkennung mit Kopieren — Blatt P7, „Fehlerkennung nur beim Fehler".
 *
 * ===========================================================================
 * 🔴 SIE IST GROEBER, ALS SIE AUSSIEHT
 * ===========================================================================
 *
 * P7 hat im Code nachgemessen und daraus die Aufschrift abgeleitet:
 *
 *   „der `digest` ist ein HASH DES FEHLERS, nicht die Kennung dieses Vorfalls.
 *    Zwei Nutzer mit demselben Programmfehler nennen dieselbe Kennung. Damit
 *    findet der Support die FEHLERART — aber nicht «dieser Kunde, gestern um
 *    14:32»."
 *
 * Deshalb heisst sie „Kennung" und nicht „Ticket" oder „Vorfall-ID", und
 * daneben steht kein Satz, der eine Antwort verspricht. Wer den Vorfall
 * einzeln finden will, braucht eine zweite Kennung mit Zeit und
 * Arbeitsbereich — „das ist ein eigener Vorgang".
 *
 * ⚠️ Der Fehler ist zu diesem Zeitpunkt bereits erfasst: jede Fehlerseite ruft
 * `useCaptureException`. Der Knopf dient dem Gespraech mit dem Support, nicht
 * der Meldung — die ist schon raus, bevor jemand ihn sieht.
 *
 * 🔴 Auf der 404 gibt es sie NICHT: „dort ist kein Fehler passiert, den man
 * melden könnte."
 */
export function FehlerKennung({ kennung }: { kennung: string }) {
  const [kopiert, setKopiert] = useState(false);

  return (
    <div
      className="text-muted-foreground flex items-center gap-1 text-xs"
      data-test="fehler-kennung"
    >
      {/*
        🔴 `select-all` — die Kennung ist auch OHNE Knopf zu bekommen.
        
        Der Kopieren-Knopf meldet bewusst nichts, wenn die Ablage fehlt oder
        ablehnt (eine falsche Zusage waere genau das, was P7 streicht). Damit
        bleibt der Mensch aber im Unklaren, ob er nochmal druecken soll —
        gemeldet aus einer Gegenpruefung am 04.09.2026.
        
        Die Antwort ist nicht eine dritte Meldung („ging nicht"), sondern ein
        Weg, der ohne die Ablage auskommt: ein Klick auf die Kennung waehlt sie
        vollstaendig aus, danach genuegt Strg/Cmd+C. Das gilt in jedem Browser
        und in jedem Zusammenhang, auch dort, wo `navigator.clipboard` fehlt.
      */}
      <span className="font-mono select-all">
        <Trans i18nKey="common:p7Kennung" values={{ kennung }} />
      </span>

      {/*
        🔴 Der Knopf verschwindet nicht, wenn das Kopieren scheitert — er sagt
        dann nur nichts Falsches. `navigator.clipboard` fehlt in unsicheren
        Zusammenhaengen und wird in manchen Browsern ohne Nutzergeste
        abgelehnt; ein „Kopiert" ohne Inhalt in der Ablage waere genau die
        Sorte Zusage, die P7 an dieser Seite streicht.
      */}
      <Button
        variant="ghost"
        size="sm"
        className="h-6 gap-1 px-2 text-xs"
        onClick={() => {
          void navigator.clipboard
            ?.writeText(kennung)
            .then(() => {
              setKopiert(true);
              setTimeout(() => setKopiert(false), 2_000);
            })
            .catch(() => setKopiert(false));
        }}
      >
        {kopiert ? (
          <>
            <Check className="h-3 w-3" aria-hidden />
            <Trans i18nKey="common:p7Kopiert" />
          </>
        ) : (
          <>
            <Copy className="h-3 w-3" aria-hidden />
            <Trans i18nKey="common:p7Kopieren" />
          </>
        )}
      </Button>
    </div>
  );
}
